import { useEffect, useState } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AddRegular, Dismiss24Regular, SignatureRegular } from '@fluentui/react-icons';

import { APPROVAL_DECISION, TApprovalDecision, TApproverRole } from '../../models';
import { TApprovalInput } from '../../state/ChangeV2Store';

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: '460px' },
  readonlyRow: { display: 'flex', flexDirection: 'column', gap: '2px' },
  muted: { color: tokens.colorNeutralForeground3 },
  conditions: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  conditionRow: { display: 'flex', gap: tokens.spacingHorizontalXS, alignItems: 'center' },
});

const MEANINGS: Record<string, string> = {
  [APPROVAL_DECISION.APPROVE]:
    'I approve the assessed scope and preliminary implementation concept to proceed to planning. This is not the final implementation release.',
  [APPROVAL_DECISION.APPROVE_WITH_CONDITIONS]:
    'I approve to proceed subject to the conditions below. This is not the final implementation release.',
  [APPROVAL_DECISION.RETURN]: 'I return this assessment to the assessors for revision.',
  [APPROVAL_DECISION.REJECT]: 'I reject this change and record the rationale.',
};

const createMockPassword = (): string => {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  return Array.from({ length: 13 }, () => letters[Math.floor(Math.random() * letters.length)]).join('');
};

type TSection = { id: string; title: string };

type TProps = {
  decision: TApprovalDecision | undefined;
  currentUser: string;
  approverRoles: TApproverRole[];
  sections: TSection[];
  rejectBlocked?: boolean;
  rejectPolicyNote?: string;
  onClose: () => void;
  onConfirm: (input: TApprovalInput) => void;
};

// A concise reauthentication (electronic-signature) dialog for the approval
// actions. The prototype captures the signer, role, meaning, decision, an optional
// comment and a reauthentication placeholder - no real authentication service.
export const ApprovalDecisionDialog = ({
  decision,
  currentUser,
  approverRoles,
  sections,
  rejectBlocked = false,
  rejectPolicyNote,
  onClose,
  onConfirm,
}: TProps) => {
  const classes = useStyles();
  const [role, setRole] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [password, setPassword] = useState('');
  const [rationale, setRationale] = useState('');
  const [affectedSections, setAffectedSections] = useState<string[]>([]);
  const [conditions, setConditions] = useState<string[]>(['']);

  useEffect(() => {
    if (decision) {
      const preferredRole =
        approverRoles.find((approver) => approver.role === 'Regulatory affairs') ?? approverRoles[0];
      setRole(preferredRole ? [preferredRole.role] : []);
      setComment('');
      setPassword(createMockPassword());
      setRationale('');
      setAffectedSections([]);
      setConditions(['']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decision]);

  if (!decision) return null;

  const isApprove = decision === APPROVAL_DECISION.APPROVE || decision === APPROVAL_DECISION.APPROVE_WITH_CONDITIONS;
  const isWithConditions = decision === APPROVAL_DECISION.APPROVE_WITH_CONDITIONS;
  const isReturn = decision === APPROVAL_DECISION.RETURN;
  const isReject = decision === APPROVAL_DECISION.REJECT;

  const trimmedConditions = conditions.map((entry) => entry.trim()).filter(Boolean);
  const canConfirm =
    role.length === 1 &&
    (!isApprove || Boolean(password.trim())) &&
    (!isWithConditions || trimmedConditions.length >= 1) &&
    (!isReturn || Boolean(rationale.trim())) &&
    (!isReject || (Boolean(rationale.trim()) && !rejectBlocked));

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm({
      decision,
      signature: { role: role[0], meaning: MEANINGS[decision], comment: comment.trim() || undefined },
      conditions: isWithConditions ? trimmedConditions : undefined,
      returnRationale: isReturn ? rationale.trim() : undefined,
      returnSections: isReturn ? affectedSections : undefined,
      rejectRationale: isReject ? rationale.trim() : undefined,
    });
  };

  const sectionLabel = sections
    .filter((section) => affectedSections.includes(section.id))
    .map((section) => section.title)
    .join(', ');

  return (
    <Dialog open onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={onClose} />
              </DialogTrigger>
            }
          >
            {decision}
          </DialogTitle>
          <DialogContent>
            <div className={classes.body}>
              <div className={classes.readonlyRow}>
                <Caption1 className={classes.muted}>User</Caption1>
                <Body1>{currentUser}</Body1>
              </div>

              <Field label="Role">
                <Dropdown
                  selectedOptions={role}
                  value={role[0] ?? ''}
                  placeholder="Select the role you are signing as"
                  onOptionSelect={(_, data) => setRole(data.selectedOptions)}
                >
                  {approverRoles.map((approver) => (
                    <Option key={approver.role} value={approver.role} text={approver.role}>
                      {approver.role}
                    </Option>
                  ))}
                </Dropdown>
              </Field>

              <div className={classes.readonlyRow}>
                <Caption1 className={classes.muted}>Meaning of signature</Caption1>
                <Body1>{MEANINGS[decision]}</Body1>
              </div>

              {isReject && rejectBlocked && rejectPolicyNote ? (
                <MessageBar intent="warning">
                  <MessageBarBody>{rejectPolicyNote}</MessageBarBody>
                </MessageBar>
              ) : null}

              {isWithConditions ? (
                <Field label="Conditions of approval" required>
                  <div className={classes.conditions}>
                    {conditions.map((condition, index) => (
                      <div key={index} className={classes.conditionRow}>
                        <Input
                          value={condition}
                          placeholder="Condition to resolve before the configured gate"
                          onChange={(_, data) =>
                            setConditions((prev) => prev.map((entry, i) => (i === index ? data.value : entry)))
                          }
                        />
                        {conditions.length > 1 ? (
                          <Button
                            appearance="subtle"
                            icon={<Dismiss24Regular />}
                            aria-label="Remove condition"
                            onClick={() => setConditions((prev) => prev.filter((_, i) => i !== index))}
                          />
                        ) : null}
                      </div>
                    ))}
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<AddRegular />}
                      onClick={() => setConditions((prev) => [...prev, ''])}
                    >
                      Add condition
                    </Button>
                  </div>
                </Field>
              ) : null}

              {isReturn ? (
                <>
                  <Field label="Revision rationale" required>
                    <Textarea
                      value={rationale}
                      resize="vertical"
                      onChange={(_, data) => setRationale(data.value)}
                      placeholder="What needs to be revised?"
                    />
                  </Field>
                  <Field label="Affected assessment sections">
                    <Dropdown
                      multiselect
                      selectedOptions={affectedSections}
                      value={sectionLabel}
                      placeholder="Select sections to revise"
                      onOptionSelect={(_, data) => setAffectedSections(data.selectedOptions)}
                    >
                      {sections.map((section) => (
                        <Option key={section.id} value={section.id} text={section.title}>
                          {section.title}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                </>
              ) : null}

              {isReject ? (
                <Field label="Rejection rationale" required>
                  <Textarea
                    value={rationale}
                    resize="vertical"
                    onChange={(_, data) => setRationale(data.value)}
                    placeholder="Why is the change rejected?"
                  />
                </Field>
              ) : null}

              <Field label="Comment (optional)">
                <Textarea
                  value={comment}
                  resize="vertical"
                  onChange={(_, data) => setComment(data.value)}
                  placeholder="Optional comment"
                />
              </Field>

              {isApprove ? (
                <Field label="Reauthentication" required>
                  <Input
                    type="password"
                    value={password}
                    onChange={(_, data) => setPassword(data.value)}
                    placeholder="Enter your password to sign"
                  />
                </Field>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button appearance="primary" icon={<SignatureRegular />} disabled={!canConfirm} onClick={handleConfirm}>
              {isApprove ? 'Sign and confirm' : 'Confirm'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
